import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { onBroadcastNotificationReceived, setupNotificationHandler } from '../Socket/socket';
import axios from 'axios';

// Maximum number of notifications to keep in memory to prevent memory leaks
// Older notifications will be automatically removed when this limit is exceeded
const MAX_NOTIFICATIONS = 100;

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();

  const addNotification = useCallback((notification) => {
    setNotifications(prev => [notification, ...prev].slice(0, MAX_NOTIFICATIONS));
    setUnreadCount(prev => prev + 1);
  }, []);

  const addNotificationRef = useRef(addNotification);
  useEffect(() => { addNotificationRef.current = addNotification; }, [addNotification]);

  // Set up the notification handler for socket messages
  useEffect(() => {
    const stableHandler = (notification) => addNotificationRef.current(notification);
    setupNotificationHandler({ addNotification: stableHandler, userId: user?.sub });
  }, [user]);

  // Listen for broadcast notifications from socket
  useEffect(() => {
    if (isAuthenticated) {
      const handleBroadcastNotification = (notification) => {
        addNotificationRef.current({
          ...notification,
          threadId: `notification-${Date.now()}`,
        });
      };

      const unsubscribe = onBroadcastNotificationReceived(handleBroadcastNotification);
      
      return () => {
        unsubscribe && unsubscribe();
      };
    }
  }, [isAuthenticated]);

  // Fetch notifications from API on mount if needed
  useEffect(() => {
    const fetchNotifications = async () => {
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          
          // This would be replaced with a real API call in production
          /* 
          const response = await axios.get(`${process.env.REACT_APP_SERVER}api/notifications`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          // Add fetched notifications to state
          const fetchedNotifications = response.data.notifications.map(notification => ({
            ...notification,
            threadId: `notification-${notification._id}` // Use MongoDB ID as unique identifier
          }));
          
          setNotifications(fetchedNotifications);
          setUnreadCount(fetchedNotifications.length);
          */
          
          // Initialize with empty notifications - removed mock data
          setNotifications([]);
          setUnreadCount(0);
        } catch (error) {
          console.error('Error fetching notifications:', error);
        }
      }
    };

    fetchNotifications();
  }, [isAuthenticated, getAccessTokenSilently]);

  const clearNotification = (threadId) => {
    setNotifications(prev => prev.filter(n => n.threadId !== threadId));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  return (
    <NotificationContext.Provider 
      value={{ 
        notifications, 
        unreadCount, 
        addNotification, 
        clearNotification,
        clearAllNotifications 
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}; 